
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BlogService } from '@/services/blog/blogService';
import { toast } from '@/hooks/use-toast';

export const useBlogOperations = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const deleteMutation = useMutation({
    mutationFn: (id: string) => BlogService.deletePost(id),
    onSuccess: () => {
      toast({
        title: 'Post deleted',
        description: 'Post has been successfully deleted',
      });
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete post',
        variant: 'destructive',
      });
    },
  });

  const handleCreatePost = () => {
    navigate('/blog/admin/create');
  };

  const handleEditPost = (id: string) => {
    navigate(`/blog/admin/edit/${id}`);
  };

  const handleViewPost = (slug: string) => {
    navigate(`/blog/${slug}`);
  };

  const handleDuplicatePost = async (id: string) => {
    try {
      await BlogService.duplicatePost(id);
      toast({
        title: 'Post duplicated',
        description: 'Post has been successfully duplicated',
      });
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to duplicate post',
        variant: 'destructive',
      });
    }
  };

  const handleDeletePost = async (id: string) => {
    deleteMutation.mutate(id);
  };

  return {
    handleCreatePost,
    handleEditPost,
    handleViewPost,
    handleDuplicatePost,
    handleDeletePost,
  };
};
